import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, FaBuilding, FaUser, FaEnvelope, FaPhone, 
  FaMapMarkerAlt, FaFileInvoice, FaTruck, FaBox, 
  FaCalendarAlt, FaDownload, FaEdit, FaTrash,
  FaFileAlt, FaCheckCircle, FaClock, FaExclamationTriangle,
  FaUpload, FaEye, FaUniversity, FaDollarSign, FaBriefcase,
  FaIdCard, FaUserTie, FaInfoCircle, FaShieldAlt,
  FaRegFilePdf, FaFileImage, FaExternalLinkAlt, FaSpinner,
  FaPlus, FaTimes, FaSave, FaStar, FaStarHalfAlt, FaRegStar,
  FaHistory, FaSync, FaUndo, FaCheck, FaUsers
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import CompanyUsersCard from '../components/CompanyUsersCard';

function ClientDetailPage({ onLogout }) {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [loads, setLoads] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentHistory, setDocumentHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [references, setReferences] = useState([]);
  const [historyModal, setHistoryModal] = useState(null); // { docType, history }
  const [deleteContactTarget, setDeleteContactTarget] = useState(null);
  
  // ✅ Edit Mode state
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin';
  const canEdit = ['admin', 'super_admin'].includes(user.role);

  useEffect(() => {
    fetchClientData();
    fetchContacts();
  }, [id]);

  // ✅ UPDATED: Removed document history API call
  const fetchClientData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch client
      const clientRes = await fetch(`${API_URL}/clients/${id}`);
      const clientData = await clientRes.json();
      if (clientData.success) {
        setClient(clientData.client);
        setEditData({
          company_name: clientData.client.company_name || '',
          trading_name: clientData.client.trading_name || '',
          registration_number: clientData.client.registration_number || '',
          vat_number: clientData.client.vat_number || '',
          company_type: clientData.client.company_type || '',
          physical_address: clientData.client.physical_address || '',
          city: clientData.client.city || '',
          province: clientData.client.province || '',
          postal_code: clientData.client.postal_code || '',
          billing_contact_person: clientData.client.billing_contact_person || '',
          billing_email: clientData.client.billing_email || '',
          billing_phone: clientData.client.billing_phone || '',
          contact_person: clientData.client.contact_person || '',
          email: clientData.client.contact_email || clientData.client.email || '',
          phone: clientData.client.contact_phone || clientData.client.phone || '',
          mobile: clientData.client.mobile || '',
          payment_terms: normaliseTerms(clientData.client.client_type || 'prepaid', clientData.client.payment_terms),
          credit_limit: clientData.client.approved_credit_limit || 0,
          client_type: clientData.client.client_type || 'prepaid'
        });
        if (clientData.client.references) {
          setReferences(clientData.client.references);
        }
      }

      // Fetch loads
      const loadsRes = await fetch(`${API_URL}/loads?client_id=${id}`);
      const loadsData = await loadsRes.json();
      if (loadsData.success) {
        setLoads(loadsData.loads || []);
      }

      // Fetch jobs
      const jobsRes = await fetch(`${API_URL}/jobs?client_id=${id}`);
      const jobsData = await jobsRes.json();
      if (jobsData.success) {
        setJobs(jobsData.jobs || []);
      }

      // Fetch invoices
      const invoicesRes = await fetch(`${API_URL}/invoices?client_id=${id}`);
      const invoicesData = await invoicesRes.json();
      if (invoicesData.success) {
        setInvoices(invoicesData.invoices || []);
      }

      // Fetch documents
      const docsRes = await fetch(`${API_URL}/documents/client/${id}`);
      const docsData = await docsRes.json();
      if (docsData.success) {
        setDocuments(docsData.documents || []);
      }

      // ⚠️ DOCUMENT HISTORY - Temporarily disabled (endpoint doesn't exist yet)
      setDocumentHistory({});

    } catch (err) {
      console.error('Error fetching client data:', err);
      setError('Failed to load client details');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Contacts state
  const [contacts, setContacts] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ contact_person: '', email: '', phone: '', role: '', is_primary: false });
  const [savingContact, setSavingContact] = useState(false);

  // ✅ Fetch contacts
  const fetchContacts = async () => {
    try {
      const response = await fetch(`${API_URL}/clients/${id}/contacts`);
      const data = await response.json();
      if (data.success) {
        setContacts(data.contacts || []);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
    }
  };

  // ✅ Handle contact save
  const handleSaveContact = async (e) => {
    e.preventDefault();
    setSavingContact(true);
    try {
      if (!contactForm.contact_person) {
        toast.error('Contact person name is required');
        setSavingContact(false);
        return;
      }
      let response;
      if (editingContact) {
        response = await fetch(`${API_URL}/clients/contacts/${editingContact.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(contactForm)
        });
      } else {
        response = await fetch(`${API_URL}/clients/${id}/contacts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(contactForm)
        });
      }
      const data = await response.json();
      if (data.success) {
        toast.success(editingContact ? '✅ Contact updated!' : '✅ Contact added!');
        setShowContactModal(false);
        setEditingContact(null);
        setContactForm({ contact_person: '', email: '', phone: '', role: '', is_primary: false });
        fetchContacts();
      } else {
        toast.error(data.error || 'Failed to save contact');
      }
    } catch (err) {
      console.error('Error saving contact:', err);
      toast.error('Failed to save contact');
    } finally {
      setSavingContact(false);
    }
  };

  // ✅ Handle contact delete
  const handleDeleteContact = (contactId) => {
    setDeleteContactTarget(contactId);
  };

  const performDeleteContact = async () => {
    if (!deleteContactTarget) return;
    try {
      const response = await fetch(`${API_URL}/clients/contacts/${deleteContactTarget}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Contact deleted!');
        fetchContacts();
      } else {
        toast.error(data.error || 'Failed to delete contact');
      }
    } catch (err) {
      console.error('Error deleting contact:', err);
      toast.error('Failed to delete contact');
    }
    setDeleteContactTarget(null);
  };

  // ✅ Handle edit mode toggle
  const toggleEditMode = () => {
    if (isEditing) {
      // Cancel editing - reset to original data
      setEditData({
        company_name: client.company_name || '',
        trading_name: client.trading_name || '',
        registration_number: client.registration_number || '',
        vat_number: client.vat_number || '',
        company_type: client.company_type || '',
        physical_address: client.physical_address || '',
        city: client.city || '',
        province: client.province || '',
        postal_code: client.postal_code || '',
        billing_contact_person: client.billing_contact_person || '',
        billing_email: client.billing_email || '',
        billing_phone: client.billing_phone || '',
        contact_person: client.contact_person || '',
        email: client.contact_email || client.email || '',
        phone: client.contact_phone || client.phone || '',
        mobile: client.mobile || '',
        payment_terms: normaliseTerms(client.client_type || 'prepaid', client.payment_terms),
        credit_limit: client.approved_credit_limit || 0,
        client_type: client.client_type || 'prepaid'
      });
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  };

  // ✅ Handle save changes - FIXED: Added 'mobile' to payload
  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const payload = {
        company_name: editData.company_name,
        trading_name: editData.trading_name,
        registration_number: editData.registration_number,
        vat_number: editData.vat_number,
        company_type: editData.company_type,
        physical_address: editData.physical_address,
        city: editData.city,
        province: editData.province,
        postal_code: editData.postal_code,
        billing_contact_person: editData.billing_contact_person,
        billing_email: editData.billing_email,
        billing_phone: editData.billing_phone,
        contact_person: editData.contact_person,
        contact_email: editData.email,
        contact_phone: editData.phone,
        mobile: editData.mobile,        // ✅ ADDED
        payment_terms: normaliseTerms(editData.client_type, editData.payment_terms),
        approved_credit_limit: editData.credit_limit,
        client_type: editData.client_type
      };

      console.log('📤 Sending payload:', payload);

      const response = await fetch(`${API_URL}/clients/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      console.log('📥 Response:', data);

      if (data.success) {
        toast.success('✅ Client updated successfully!');
        setIsEditing(false);
        fetchClientData();
      } else {
        toast.error(data.error || 'Failed to update client');
      }
    } catch (err) {
      console.error('Error updating client:', err);
      toast.error('Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  // ✅ Handle document replace (upload new version)
  const handleReplaceDocument = async (docType, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('document', file);
    formData.append('type', docType);

    try {
      const response = await fetch(`${API_URL}/documents/client/${id}/replace`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`✅ ${docType} replaced successfully!`);
        fetchClientData();
      } else {
        toast.error(data.error || 'Failed to replace document');
      }
    } catch (err) {
      console.error('Error replacing document:', err);
      toast.error('Failed to replace document');
    }
  };

  // ✅ Handle document download - ACTUALLY DOWNLOAD
  const downloadDocument = async (url, filename) => {
    if (!url) {
      toast.warning('No document to download');
      return;
    }

    try {
      const fullUrl = getDocumentUrl(url);
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download document');
    }
  };

  // ✅ View document history (uses local state, no API call)
  const viewDocumentHistory = (docType) => {
    const history = documentHistory[docType] || [];
    if (history.length === 0) {
      toast.info('No previous versions found');
      return;
    }

    setHistoryModal({ docType, history });
  };

  const getStatusBadge = (status) => {
    const styles = {
      'active': 'bg-green-100 text-green-600',
      'inactive': 'bg-red-100 text-red-600',
      'pending': 'bg-yellow-100 text-yellow-600',
      'rejected': 'bg-red-100 text-red-600',
      'paid': 'bg-green-100 text-green-600',
      'overdue': 'bg-red-100 text-red-600',
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status?.toLowerCase()] || styles.pending}`;
  };

  const getClientTypeBadge = (type) => {
    const styles = {
      'prepaid': 'bg-blue-100 text-blue-600',
      'credit': 'bg-purple-100 text-purple-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[type] || 'bg-gray-100 text-gray-600'}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'R 0.00';
    return `R ${parseFloat(amount).toFixed(2)}`;
  };

  const getDocumentUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return window.getServerUrl(url);
  };

  const viewDocument = (docUrl, docName) => {
    if (!docUrl) {
      toast.warning('No document uploaded');
      return;
    }
    const fullUrl = getDocumentUrl(docUrl);
    if (fullUrl) {
      window.open(fullUrl, '_blank');
    } else {
      toast.error('Could not open document');
    }
  };

  const getDocumentIcon = (url) => {
    if (!url) return <FaFileAlt className="text-gray-400" />;
    if (url.toLowerCase().includes('.pdf')) return <FaRegFilePdf className="text-red-500" />;
    if (url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg') || url.toLowerCase().includes('.png')) {
      return <FaFileImage className="text-blue-500" />;
    }
    return <FaFileAlt className="text-gray-400" />;
  };

  const getDocumentLabel = (url) => {
    if (!url) return 'No document';
    if (url.toLowerCase().includes('.pdf')) return 'PDF';
    if (url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg')) return 'JPEG';
    if (url.toLowerCase().includes('.png')) return 'PNG';
    return 'Document';
  };

  const getClientDocuments = () => {
    if (!client) return [];
    const docMap = [
      { key: 'proof_of_account_url', label: 'Proof of Account', type: 'proof_of_account' },
      { key: 'cipc_document_url', label: 'CIPC Document', type: 'cipc_document' },
      { key: 'director_id_url', label: 'Director ID', type: 'director_id' },
      { key: 'proof_of_address_url', label: 'Proof of Address', type: 'proof_of_address' },
      { key: 'bank_statements_url', label: 'Bank Statements', type: 'bank_statements' },
    ];

    const docs = [];
    for (const doc of docMap) {
      if (client[doc.key]) {
        docs.push({
          label: doc.label,
          url: client[doc.key],
          key: doc.key,
          type: doc.type,
          icon: doc.icon,
          uploaded_at: client[`${doc.type}_uploaded_at`] || client.created_at
        });
      }
    }
    return docs;
  };

  const getDocVerified = (field) => {
    if (!client?.verification_details) return false;
    return client.verification_details[field] === true;
  };

  const isCredit = client?.client_type === 'credit';
  const totalSpent = invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const totalTonnage = loads.reduce((sum, l) => sum + (l.weight_mt || 0), 0);
  const activeJobs = jobs.filter(j => j.status === 'active' || j.status === 'in_progress').length;
  const completedJobs = jobs.filter(j => j.status === 'completed').length;
  const pendingInvoices = invoices.filter(i => i.status === 'pending' || i.status === 'pending_payment').length;
  const paidInvoices = invoices.filter(i => i.status === 'paid').length;
  const clientDocs = getClientDocuments();

  const PREPAID_TERMS = ['Pay in Advance (Prepaid)', 'COD'];
  const CREDIT_TERMS = ['7 Days', '15 Days', '30 Days', '50% Booking/50% EPOD', '50% Booking/50% Completion'];
  const DEFAULT_PREPAID_TERM = 'Pay in Advance (Prepaid)';
  const termOptionsFor = (type) => (type === 'credit' ? CREDIT_TERMS : PREPAID_TERMS);
  // Prepaid and credit have DIFFERENT term lists - a prepaid client must never carry
  // "30 Days". That mismatch is exactly why Intagrey read "30 days" while marked Prepaid:
  // client_type and payment_terms were two completely independent fields, so choosing
  // Prepaid never changed the terms (which default to '30 days' in the database).
  const matchTerm = (list, t) => list.find((x) => x.toLowerCase() === String(t || '').trim().toLowerCase());
  const normaliseTerms = (type, terms) => {
    const t = String(terms || '').trim();
    if (type === 'credit') return matchTerm(CREDIT_TERMS, t) || '30 Days';
    return matchTerm(PREPAID_TERMS, t) || DEFAULT_PREPAID_TERM;
  };

  if (loading) {
    return (
      <AdminLayout activeTab="clients" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading client details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !client) {
    return (
      <AdminLayout activeTab="clients" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Client</h2>
          <p className="text-gray-500">{error || 'Client not found'}</p>
          <Link to="/clients" className="mt-4 inline-block bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors">
            <FaArrowLeft className="inline mr-2" /> Back to Clients
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="clients" onLogout={onLogout} user={user}>
      {/* Back Button */}
      <Link to="/clients" className="inline-flex items-center gap-2 text-navy hover:text-green transition-colors mb-6">
        <FaArrowLeft size={16} /> Back to Clients
      </Link>

      {/* Client Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-navy">{client.company_name}</h1>
              <span className={getStatusBadge(client.status)}>{client.status || 'active'}</span>
              <span className={getClientTypeBadge(client.client_type)}>
                {client.client_type || 'prepaid'}
              </span>
              {isCredit && client.approved_credit_limit > 0 && (
                <span className="px-2 py-1 bg-purple-50 text-purple-600 rounded-full text-xs font-semibold">
                  Limit: {formatCurrency(client.approved_credit_limit)}
                </span>
              )}
            </div>
            <p className="text-gray-500 text-sm mt-1">
              Reg: {client.registration_number || 'N/A'} • Member since: {formatDate(client.created_at)}
            </p>
          </div>
          <div className="flex gap-2">
            {canEdit && (
              <>
                <button
                  onClick={toggleEditMode}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                    isEditing 
                      ? 'border border-red-300 text-red-600 hover:bg-red-50' 
                      : 'bg-navy text-white hover:bg-green hover:text-navy'
                  }`}
                >
                  <FaEdit size={14} /> {isEditing ? 'Cancel' : 'Edit'}
                </button>
                <button className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:border-red-500 hover:bg-red-50 transition-colors">
                  <FaTrash size={14} /> Delete
                </button>
              </>
            )}
          </div>
        </div>

        {/* ✅ Edit Mode Banner */}
        {isEditing && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex justify-between items-center">
            <span className="text-sm text-yellow-700 flex items-center gap-2">
              <FaEdit /> You are in Edit Mode. Update the fields below and click "Save Changes".
            </span>
            <button
              onClick={handleSaveChanges}
              disabled={saving}
              style={{
                padding: '8px 16px',
                backgroundColor: '#00A86B',
                color: 'white',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: saving ? 0.5 : 1
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
            >
              {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}

        {/* KPI Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Total Spent</p>
            <p className="text-2xl font-bold text-navy">{formatCurrency(totalSpent)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Total Tonnage</p>
            <p className="text-2xl font-bold text-navy">{totalTonnage.toFixed(0)} MT</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Active Jobs</p>
            <p className="text-2xl font-bold text-navy">{activeJobs}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Completed Jobs</p>
            <p className="text-2xl font-bold text-navy">{completedJobs}</p>
          </div>
        </div>
      </div>

      {/* Client Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Company Information - Always read-only */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
            <FaBuilding className="text-green-500" /> Company Information
            {isEditing && <span className="text-xs text-green-600 ml-2">(Editing)</span>}
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Company Name</span>
              {isEditing ? <input type="text" value={editData.company_name} onChange={(e) => setEditData({ ...editData, company_name: e.target.value })} className="w-1/2 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green" /> : <span className="font-medium text-navy">{client.company_name}</span>}
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Trading Name</span>
              {isEditing ? <input type="text" value={editData.trading_name} onChange={(e) => setEditData({ ...editData, trading_name: e.target.value })} className="w-1/2 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green" /> : <span className="font-medium text-navy">{client.trading_name || 'N/A'}</span>}
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Registration Number</span>
              {isEditing ? <input type="text" value={editData.registration_number} onChange={(e) => setEditData({ ...editData, registration_number: e.target.value })} className="w-1/2 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green" /> : <span className="font-medium text-navy">{client.registration_number || 'N/A'}</span>}
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">VAT Number</span>
              {isEditing ? <input type="text" value={editData.vat_number} onChange={(e) => setEditData({ ...editData, vat_number: e.target.value })} className="w-1/2 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green" /> : <span className="font-medium text-navy">{client.vat_number || 'N/A'}</span>}
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Company Type</span>
              {isEditing ? <input type="text" value={editData.company_type} onChange={(e) => setEditData({ ...editData, company_type: e.target.value })} className="w-1/2 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green" /> : <span className="font-medium text-navy">{client.company_type || 'N/A'}</span>}
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Address</span>
              {isEditing ? <input type="text" value={editData.physical_address} onChange={(e) => setEditData({ ...editData, physical_address: e.target.value })} className="w-1/2 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green" /> : <span className="font-medium text-navy text-right">{client.physical_address || 'N/A'}</span>}
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">City / Province / Postal</span>
              {isEditing ? (
                <div className="flex gap-1">
                  <input type="text" value={editData.city} placeholder="City" onChange={(e) => setEditData({ ...editData, city: e.target.value })} className="w-1/3 px-2 py-1 border border-gray-300 rounded-lg text-sm" />
                  <input type="text" value={editData.province} placeholder="Province" onChange={(e) => setEditData({ ...editData, province: e.target.value })} className="w-1/3 px-2 py-1 border border-gray-300 rounded-lg text-sm" />
                  <input type="text" value={editData.postal_code} placeholder="Postal" onChange={(e) => setEditData({ ...editData, postal_code: e.target.value })} className="w-1/3 px-2 py-1 border border-gray-300 rounded-lg text-sm" />
                </div>
              ) : <span className="font-medium text-navy text-right">{[client.city, client.province, client.postal_code].filter(Boolean).join(', ') || 'N/A'}</span>}
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-500">Status</span>
              <span className="font-medium text-navy">{client.status || 'pending'}</span>
            </div>
          </div>
        </div>

        {/* Contact Information - Editable */}
        <div className={`bg-white rounded-xl border p-6 ${isEditing ? 'border-green-400 shadow-md' : 'border-gray-200'}`}>
          <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
            <FaUser className="text-green-500" /> Contact Information
            {isEditing && <span className="text-xs text-green-600 ml-2">(Editing)</span>}
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Contact Person</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editData.contact_person}
                  onChange={(e) => setEditData({ ...editData, contact_person: e.target.value })}
                  className="w-1/2 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green"
                />
              ) : (
                <span className="font-medium text-navy">{client.contact_person || 'N/A'}</span>
              )}
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Email</span>
              {isEditing ? (
                <input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  className="w-1/2 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green"
                />
              ) : (
                <span className="font-medium text-navy">{client.contact_email || client.email || 'N/A'}</span>
              )}
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Phone</span>
              {isEditing ? (
                <input
                  type="tel"
                  value={editData.phone}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                  className="w-1/2 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green"
                />
              ) : (
                <span className="font-medium text-navy">{client.contact_phone || client.phone || 'N/A'}</span>
              )}
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-500">Mobile</span>
              {isEditing ? (
                <input
                  type="tel"
                  value={editData.mobile}
                  onChange={(e) => setEditData({ ...editData, mobile: e.target.value })}
                  className="w-1/2 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green"
                />
              ) : (
                <span className="font-medium text-navy">{client.mobile || 'N/A'}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ✅ PHASE 2 — the logins that belong to this client (add / remove team members) */}
      <div className="mb-6">
        <CompanyUsersCard
          companyType="client"
          companyId={id}
          companyName={client?.company_name}
          className="rounded-xl border p-6"
        />
      </div>

      {/* ✅ Contacts Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-navy flex items-center gap-2">
            <FaUsers className="text-green-500" /> Contacts ({contacts.length})
          </h3>
          {isSuperAdmin && (
            <button
              onClick={() => {
                setEditingContact(null);
                setContactForm({ contact_person: '', email: '', phone: '', role: '', is_primary: false });
                setShowContactModal(true);
              }}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
            >
              <FaPlus size={12} /> Add Contact
            </button>
          )}
        </div>
        {contacts.length === 0 ? (
          <p className="text-gray-400 text-sm">No additional contacts added yet</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {contacts.map(contact => (
              <div key={contact.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-sm font-bold">
                      {contact.contact_person.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-navy text-sm">{contact.contact_person}</p>
                      <span className="text-xs text-gray-400">{contact.role || 'General'}</span>
                    </div>
                  </div>
                  {contact.is_primary && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Primary</span>
                  )}
                </div>
                <div className="mt-3 space-y-1 text-sm">
                  {contact.email && <p className="flex items-center gap-2 text-gray-600"><FaEnvelope size={12} className="text-gray-400" /> {contact.email}</p>}
                  {contact.phone && <p className="flex items-center gap-2 text-gray-600"><FaPhone size={12} className="text-gray-400" /> {contact.phone}</p>}
                </div>
                {isSuperAdmin && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => {
                        setEditingContact(contact);
                        setContactForm({
                          contact_person: contact.contact_person || '',
                          email: contact.email || '',
                          phone: contact.phone || '',
                          role: contact.role || '',
                          is_primary: contact.is_primary || false
                        });
                        setShowContactModal(true);
                      }}
                      className="text-xs flex items-center gap-1 bg-blue-50 text-blue-600 px-2 py-1 rounded-lg hover:bg-blue-100"
                    >
                      <FaEdit size={10} /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteContact(contact.id)}
                      className="text-xs flex items-center gap-1 bg-red-50 text-red-600 px-2 py-1 rounded-lg hover:bg-red-100"
                    >
                      <FaTrash size={10} /> Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ✅ Add/Edit Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowContactModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-navy">{editingContact ? 'Edit Contact' : 'Add Contact'}</h2>
              <button onClick={() => setShowContactModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            <form onSubmit={handleSaveContact} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person *</label>
                  <input
                    type="text"
                    value={contactForm.contact_person}
                    onChange={(e) => setContactForm({ ...contactForm, contact_person: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. John Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. john@company.co.za"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. +27 82 123 4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <input
                    type="text"
                    value={contactForm.role}
                    onChange={(e) => setContactForm({ ...contactForm, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. Operations Manager, Billing, Transport"
                  />
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={contactForm.is_primary}
                    onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Primary Contact</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowContactModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingContact}
                  className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {savingContact ? <FaSpinner className="animate-spin" size={14} /> : <FaSave size={14} />}
                  {savingContact ? 'Saving...' : 'Save Contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Banking Details - Always read-only */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
          <FaUniversity className="text-green-500" /> Banking Details
          <span className="text-xs text-gray-400 ml-2">(Read-only)</span>
        </h3>
        {client.bank_name || client.account_holder ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Bank Name</span>
              <span className="text-sm font-medium text-navy">{client.bank_name || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Account Holder</span>
              <span className="text-sm font-medium text-navy">{client.account_holder || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Account Number</span>
              <span className="text-sm font-medium text-navy">{client.account_number || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Branch Code</span>
              <span className="text-sm font-medium text-navy">{client.branch_code || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-sm text-gray-500">Account Type</span>
              <span className="text-sm font-medium text-navy">{client.account_type || 'N/A'}</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4">No banking details provided</p>
        )}
        {client.proof_of_account_url && (
          <div className="mt-3 flex items-center gap-2">
            <FaCheckCircle className="text-green-500" size={14} />
            <span className="text-sm text-green-600">Proof of Account uploaded</span>
            <button
              onClick={() => viewDocument(client.proof_of_account_url, 'Proof of Account')}
              className="text-xs text-blue-600 hover:underline ml-2"
            >
              View
            </button>
          </div>
        )}
      </div>

      {/* Credit Information - Editable */}
      <div className={`bg-white rounded-xl border p-6 mb-6 ${isEditing ? 'border-green-400 shadow-md' : 'border-gray-200'}`}>
        <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
          <FaDollarSign className="text-purple-500" /> Credit / Payment Settings
          {isEditing && <span className="text-xs text-green-600 ml-2">(Editing)</span>}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Client Type</span>
            {isEditing ? (
              <select
                value={editData.client_type}
                onChange={(e) => {
                  const nextType = e.target.value;
                  // Switching the type MUST switch the terms too - otherwise a Prepaid
                  // client silently keeps "30 Days" (the original Intagrey bug).
                  setEditData((prev) => ({
                    ...prev,
                    client_type: nextType,
                    payment_terms: normaliseTerms(nextType, prev.payment_terms)
                  }));
                }}
                className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green"
              >
                <option value="credit">Credit</option>
                <option value="prepaid">Prepaid</option>
              </select>
            ) : (
              <span className={`text-sm font-medium ${isCredit ? 'text-purple-700' : 'text-blue-600'}`}>
                {isCredit ? 'Credit' : 'Prepaid'}
              </span>
            )}
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Payment Terms</span>
            {isEditing ? (
              <select
                value={editData.payment_terms}
                onChange={(e) => setEditData({ ...editData, payment_terms: e.target.value })}
                className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green"
              >
                {termOptionsFor(editData.client_type).map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ) : (
              <span className="text-sm font-medium text-navy">{isCredit ? (client.payment_terms || '30 Days') : 'Prepaid'}</span>
            )}
          </div>
          {isCredit && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Approved Credit Limit</span>
              {isEditing ? (
                <input
                  type="number"
                  value={editData.credit_limit}
                  onChange={(e) => setEditData({ ...editData, credit_limit: parseFloat(e.target.value) || 0 })}
                  className="w-1/2 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green"
                />
              ) : (
                <span className="text-sm font-medium text-green-700">{formatCurrency(client.approved_credit_limit)}</span>
              )}
            </div>
          )}
          {isCredit && (
            <div className="flex justify-between py-2">
              <span className="text-sm text-gray-600">Requested Limit</span>
              <span className="text-sm font-medium text-purple-700">{formatCurrency(client.requested_credit_limit)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Trade References */}
      {isCredit && references && references.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
            <FaBriefcase className="text-green-500" /> Trade References
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {references.map((ref, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="font-medium text-navy">{ref.company_name}</p>
                <div className="space-y-1 text-sm mt-2">
                  <p><span className="text-gray-500">Contact:</span> {ref.contact_person}</p>
                  <p><span className="text-gray-500">Phone:</span> {ref.phone}</p>
                  {ref.email && <p><span className="text-gray-500">Email:</span> {ref.email}</p>}
                  {ref.relationship && <p><span className="text-gray-500">Relationship:</span> {ref.relationship}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-navy flex items-center gap-2">
            <FaFileAlt className="text-green-500" /> Documents ({clientDocs.length})
          </h3>
        </div>
        {clientDocs.length === 0 ? (
          <p className="text-gray-400 text-sm">No documents uploaded</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clientDocs.map((doc) => (
              <div key={doc.key} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    {getDocumentIcon(doc.url)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy truncate">{doc.label}</p>
                    <span className="text-xs text-gray-400">{getDocumentLabel(doc.url)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => viewDocument(doc.url, doc.label)}
                    className="flex-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                  >
                    <FaEye size={12} /> View
                  </button>
                  <button
                    onClick={() => downloadDocument(doc.url, `${doc.label}.pdf`)}
                    className="flex-1 px-3 py-1.5 bg-gray-50 text-gray-600 rounded-lg text-xs hover:bg-gray-100 transition-colors flex items-center justify-center gap-1"
                  >
                    <FaDownload size={12} /> Download
                  </button>
                  {isSuperAdmin && (
                    <>
                      <button
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = '.pdf,.jpg,.jpeg,.png';
                          input.onchange = (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleReplaceDocument(doc.type, file);
                            }
                          };
                          input.click();
                        }}
                        className="px-3 py-1.5 bg-yellow-50 text-yellow-600 rounded-lg text-xs hover:bg-yellow-100 transition-colors flex items-center justify-center gap-1"
                      >
                        <FaSync size={12} /> Replace
                      </button>
                      <button
                        onClick={() => viewDocumentHistory(doc.type)}
                        className="px-3 py-1.5 bg-purple-50 text-purple-600 rounded-lg text-xs hover:bg-purple-100 transition-colors flex items-center justify-center gap-1"
                      >
                        <FaHistory size={12} /> History
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Verification Status */}
      {client.verification_details && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
            <FaShieldAlt className="text-green-500" /> Document Verification Status
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${getDocVerified('proof_of_account_verified') ? 'bg-green-500' : 'bg-gray-300'}`}></span>
              <span className="text-sm">Proof of Account {getDocVerified('proof_of_account_verified') ? '✅ Verified' : '⏳ Pending'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${getDocVerified('cipc_document_verified') ? 'bg-green-500' : 'bg-gray-300'}`}></span>
              <span className="text-sm">CIPC Document {getDocVerified('cipc_document_verified') ? '✅ Verified' : '⏳ Pending'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${getDocVerified('director_id_verified') ? 'bg-green-500' : 'bg-gray-300'}`}></span>
              <span className="text-sm">Director ID {getDocVerified('director_id_verified') ? '✅ Verified' : '⏳ Pending'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${getDocVerified('proof_of_address_verified') ? 'bg-green-500' : 'bg-gray-300'}`}></span>
              <span className="text-sm">Proof of Address {getDocVerified('proof_of_address_verified') ? '✅ Verified' : '⏳ Pending'}</span>
            </div>
            {isCredit && (
              <>
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${getDocVerified('bank_statements_verified') ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                  <span className="text-sm">Bank Statements {getDocVerified('bank_statements_verified') ? '✅ Verified' : '⏳ Pending'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${getDocVerified('references_verified') ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                  <span className="text-sm">References {getDocVerified('references_verified') ? '✅ Verified' : '⏳ Pending'}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Admin Notes */}
      {client.admin_notes && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
            <FaInfoCircle className="text-green-500" /> Admin Notes
          </h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{client.admin_notes}</p>
        </div>
      )}

      {/* Invoices Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-navy flex items-center gap-2">
            <FaFileInvoice className="text-green-500" /> Invoices
          </h3>
          <div className="flex gap-4 text-sm">
            <span className="text-gray-500">Pending: <span className="font-medium text-yellow-600">{pendingInvoices}</span></span>
            <span className="text-gray-500">Paid: <span className="font-medium text-green-600">{paidInvoices}</span></span>
          </div>
        </div>
        {invoices.length === 0 ? (
          <p className="text-gray-400 text-sm">No invoices found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice #</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.slice(0, 5).map((inv) => (
                  <tr key={inv.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm font-medium text-navy">{inv.invoice_number}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{formatDate(inv.invoice_date)}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{formatDate(inv.due_date)}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{formatCurrency(inv.total_amount)}</td>
                    <td className="px-4 py-2">
                      <span className={getStatusBadge(inv.status)}>{inv.status}</span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-2">
                        <button className="text-gray-400 hover:text-blue-600 transition-colors" title="View">
                          <FaEye size={14} />
                        </button>
                        <button className="text-gray-400 hover:text-green-600 transition-colors" title="Download">
                          <FaDownload size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
        {/* Document History Modal */}
        <Modal
          isOpen={!!historyModal}
          onClose={() => setHistoryModal(null)}
          title={historyModal ? `📄 Document History — ${historyModal.docType}` : 'Document History'}
        >
          <ul className="space-y-2">
            {historyModal?.history.map((doc, index) => (
              <li key={index} className="text-sm text-gray-600 border-b border-gray-100 pb-2">
                Version {doc.version}: Uploaded {new Date(doc.created_at).toLocaleDateString()} by {doc.uploaded_by || 'System'}
              </li>
            ))}
          </ul>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => setHistoryModal(null)}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </Modal>

        {/* Delete Contact Confirm */}
        <ConfirmDialog
          isOpen={!!deleteContactTarget}
          onClose={() => setDeleteContactTarget(null)}
          title="Delete Contact"
          message="Are you sure you want to delete this contact?"
          confirmLabel="Delete"
          danger
          onConfirm={performDeleteContact}
        />
    </AdminLayout>
  );
}

export default ClientDetailPage;